import { useQuery } from '@pinia/colada'
import { createSharedComposable, useLocalStorage } from '@vueuse/core'
import { isTokenFresh, useAuthToken } from '~/queries/auth/token'

/** Metadata the chat attaches to a user turn generated by an A2UI button. */
export interface AgentChatMessageMetadata {
  a2uiAction?: string
}

/**
 * The app-wide agent chat, backed by the user's UserAgent (Think) over the
 * shared UserAgent WebSocket. Each turn's `body` carries the model choice, the
 * page the user is on, and their access token so the agent's PAAPI and Nucleus
 * tools act as them; the agent validates all three.
 */
function _useUserAgentChat() {
  const {
    connection,
    fetchChatModels,
    fetchChatMessages,
    fetchChatHistory,
    startNewChat,
    restoreChat,
  } = useUserAgent()
  const { $groundcover } = useNuxtApp()
  const route = useRoute()
  const { data: authToken, refetch: refetchAuthToken } = useAuthToken()
  const chat = useAgentChat(connection)

  const { data: catalog } = useQuery({
    key: ['user-agent', 'chat-models'],
    query: fetchChatModels,
    staleTime: Infinity,
    /* A persisted list outlives deploys that add or remove models. */
    persist: false,
  })
  const models = computed(() => catalog.value?.models ?? [])

  const { data: historyData, refresh: refreshHistory } = useQuery({
    key: ['user-agent', 'chat-history'],
    query: fetchChatHistory,
    persist: false,
  })
  const history = computed(() => historyData.value ?? [])

  const storedModel = useLocalStorage<string | null>(
    '@admin/agent-chat-model',
    null,
  )
  const model = computed<string | undefined>({
    get: () =>
      models.value.some(({ id }) => id === storedModel.value)
        ? storedModel.value!
        : (catalog.value?.default ?? undefined),
    set: (id) => {
      storedModel.value = id ?? null
    },
  })
  const open = useLocalStorage('@admin/agent-chat-open', false)

  const turnBody = async () => ({
    model: model.value,
    token: isTokenFresh(authToken.value)
      ? authToken.value
      : (await refetchAuthToken()).data,
    page: {
      path: route.path,
      name: String(route.name ?? ''),
      title: document.title,
      params: route.params,
    },
  })

  /* The connect-time transcript frame can arrive before this chat exists. */
  fetchChatMessages()
    .then((messages) => {
      if (!chat.messages.length) chat.messages = messages
    })
    .catch((error: unknown) => $groundcover?.captureException(error as Error))

  const send = async (text: string, metadata?: AgentChatMessageMetadata) =>
    chat.sendMessage({ text, metadata }, { body: await turnBody() })

  const regenerate = async () => chat.regenerate({ body: await turnBody() })

  /* The agent archives the active chat and broadcasts the new transcript, so
   * every open tab follows; clearing locally just makes the panel feel instant. */
  const clear = async () => {
    await chat.stop()
    const previous = [...chat.messages]
    chat.messages = []
    try {
      await startNewChat()
    } catch {
      /* The server may or may not have archived before failing: trust it. */
      chat.messages = await fetchChatMessages().catch(() => previous)
      return
    }
    await refreshHistory()
  }

  const restore = async (id: string) => {
    await chat.stop()
    await restoreChat(id)
    await refreshHistory()
  }

  return {
    chat,
    models,
    model,
    open,
    history,
    send,
    regenerate,
    clear,
    restore,
  }
}

export const useUserAgentChat = createSharedComposable(_useUserAgentChat)
